import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Pencil, Camera, X, Check, ArrowUpRight, Heart } from "lucide-react";
import { toast } from "sonner";

export default function Profile() {
    const { user, ready, refresh } = useAuth();
    const [styles, setStyles] = useState([]);
    const [looks, setLooks] = useState([]);
    const [following, setFollowing] = useState([]);
    const [editing, setEditing] = useState(false);
    const [name, setName] = useState("");
    const [bio, setBio] = useState("");
    const [tags, setTags] = useState([]);
    const [avatar, setAvatar] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get("/styles").then((r) => setStyles(r.data || [])).catch(() => {});
    }, []);

    useEffect(() => {
        if (!ready || !user || user.role !== "user") return;
        api.get(`/users/${user.id}`).then((r) => {
            setLooks(r.data.saved_looks || []);
            setFollowing(r.data.following || []);
        }).catch(() => {});
    }, [ready, user]);

    if (!ready || !user) return null;

    const startEdit = () => {
        setName(user.name || "");
        setBio(user.bio || "");
        setTags(user.style_identity || []);
        setAvatar(user.avatar_url || "");
        setEditing(true);
    };

    const uploadAvatar = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        const fd = new FormData();
        fd.append("file", f);
        try {
            const { data } = await api.post("/upload/image", fd);
            setAvatar(data.url);
            toast.success("Photo uploaded — hit save");
        } catch { toast.error("Upload failed"); }
    };

    const toggleTag = (t) =>
        setTags((cur) => (cur.includes(t) ? cur.filter((x) => x !== t) : cur.length < 6 ? [...cur, t] : cur));

    const save = async () => {
        setSaving(true);
        try {
            await api.put("/me/profile", { name, bio, avatar_url: avatar, style_identity: tags });
            await refresh();
            setEditing(false);
            toast.success("Profile updated");
        } catch { toast.error("Could not save"); }
        finally { setSaving(false); }
    };

    const removeLook = async (id) => {
        try {
            await api.delete(`/me/saved-looks/${id}`);
            setLooks((l) => l.filter((x) => x.id !== id));
            toast.success("Removed");
        } catch { toast.error("Try again"); }
    };

    const styleLabel = (t) => styles.find((s) => s.id === t)?.label || t;

    const AvatarBlock = ({ size = "w-28 h-28 md:w-36 md:h-36", text = "text-5xl" }) =>
        (editing ? avatar : user.avatar_url) ? (
            <img src={editing ? avatar : user.avatar_url} alt={user.name}
                className={`${size} rounded-full object-cover border-2 border-brand-saffron`} />
        ) : (
            <div className={`${size} rounded-full bg-brand-ink border-2 border-white/15 flex items-center justify-center`}>
                <span className={`font-serif-editorial italic ${text} text-white/25`}>{user.name?.[0] || "P"}</span>
            </div>
        );

    return (
        <main className="max-w-[1400px] mx-auto px-6 md:px-10 py-14 md:py-20 min-h-screen">
            <p className="font-mono-tag text-xs uppercase text-brand-saffron mb-3">/ your profile</p>

            {/* Header card */}
            <div className="grain-card rounded-md p-6 md:p-10 flex flex-col md:flex-row gap-6 md:gap-10 md:items-center">
                <div className="relative w-fit">
                    <AvatarBlock />
                    {editing && (
                        <label data-testid="avatar-upload-label" className="absolute -bottom-1 -right-1 p-2.5 rounded-full bg-brand-saffron text-black cursor-pointer hover:bg-brand-saffron/90 transition-colors">
                            <Camera className="w-4 h-4" />
                            <input type="file" accept="image/*" className="hidden" onChange={uploadAvatar} data-testid="avatar-upload-input" />
                        </label>
                    )}
                </div>
                <div className="flex-1 min-w-0">
                    {editing ? (
                        <div className="space-y-4 max-w-lg">
                            <input
                                data-testid="profile-name-input"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                maxLength={80}
                                className="w-full bg-white/5 border border-white/15 rounded-md px-4 py-3 font-display font-bold text-2xl tracking-tight text-white focus:border-brand-saffron outline-none"
                                placeholder="Display name"
                            />
                            <textarea
                                data-testid="profile-bio-input"
                                value={bio}
                                onChange={(e) => setBio(e.target.value)}
                                maxLength={280}
                                rows={3}
                                className="w-full bg-white/5 border border-white/15 rounded-md px-4 py-3 text-sm text-white/80 focus:border-brand-saffron outline-none resize-none"
                                placeholder="Short bio — what do you wear? (280 chars)"
                            />
                        </div>
                    ) : (
                        <>
                            <h1 data-testid="profile-name" className="font-display font-extrabold text-4xl md:text-6xl tracking-tighter uppercase leading-[0.95] break-words">
                                {user.name}
                            </h1>
                            <p data-testid="profile-bio" className="text-white/70 text-base mt-3 max-w-xl">
                                {user.bio || <span className="italic text-white/40">No bio yet — tell the world what you wear.</span>}
                            </p>
                        </>
                    )}

                    {/* Style identity */}
                    <div className="mt-6">
                        <p className="font-mono-tag text-[10px] uppercase text-white/40 mb-2">/ style identity {editing && "· pick up to 6"}</p>
                        <div className="flex flex-wrap gap-2" data-testid="style-identity">
                            {(editing ? styles : (user.style_identity || []).map((t) => ({ id: t, label: styleLabel(t) }))).map((s) => {
                                const active = editing ? tags.includes(s.id) : true;
                                const disabled = editing && !active && tags.length >= 6;
                                return editing ? (
                                    <button key={s.id} data-testid={`style-tag-${s.id}`} onClick={() => toggleTag(s.id)} disabled={disabled}
                                        className={`font-mono-tag text-xs uppercase px-3 py-1.5 rounded-sm transition-colors ${
                                            active ? "bg-brand-saffron text-black" : disabled ? "border border-white/10 text-white/25 cursor-not-allowed" : "border border-white/20 text-white/60 hover:border-brand-saffron hover:text-white"
                                        }`}>
                                        {s.label}
                                    </button>
                                ) : (
                                    <span key={s.id} className="font-mono-tag text-xs uppercase px-3 py-1.5 rounded-sm bg-brand-saffron text-black">
                                        {s.label}
                                    </span>
                                );
                            })}
                            {!editing && !(user.style_identity || []).length && (
                                <span className="text-white/40 text-sm italic">No style tags yet.</span>
                            )}
                        </div>
                    </div>
                </div>
                <div className="flex md:flex-col gap-2">
                    {editing ? (
                        <>
                            <Button data-testid="profile-save-btn" onClick={save} disabled={saving}
                                className="bg-brand-saffron text-black hover:bg-brand-saffron/90 font-semibold rounded-md">
                                <Check className="w-4 h-4 mr-1" /> {saving ? "Saving…" : "Save"}
                            </Button>
                            <Button data-testid="profile-cancel-btn" onClick={() => setEditing(false)} variant="outline"
                                className="border-white/20 text-white bg-transparent hover:bg-white/10 rounded-md">
                                <X className="w-4 h-4 mr-1" /> Cancel
                            </Button>
                        </>
                    ) : (
                        <Button data-testid="profile-edit-btn" onClick={startEdit} variant="outline"
                            className="border-white/20 text-white bg-transparent hover:border-brand-saffron hover:text-brand-saffron rounded-md">
                            <Pencil className="w-4 h-4 mr-1" /> Edit profile
                        </Button>
                    )}
                </div>
            </div>

            {/* Saved looks */}
            <section className="mt-14">
                <p className="font-mono-tag text-xs uppercase text-white/50 mb-4">/ saved looks ({looks.length})</p>
                {looks.length === 0 ? (
                    <div className="border border-dashed border-white/15 rounded-md py-16 text-center">
                        <p className="font-display text-2xl mb-2">No saved looks yet.</p>
                        <p className="text-white/60 text-sm mb-6">Tap the bookmark on any brand photo or drop to pin it here.</p>
                        <Link to="/drops"><Button className="bg-brand-saffron text-black hover:bg-brand-saffron/90 font-semibold rounded-md">Browse drops</Button></Link>
                    </div>
                ) : (
                    <div data-testid="saved-looks-grid" className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {looks.map((l) => (
                            <div key={l.id} data-testid={`saved-look-${l.id}`} className="relative aspect-square rounded-md overflow-hidden border border-white/10 group">
                                <img src={l.image_url} alt={l.title} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                                <button data-testid={`remove-look-${l.id}`} onClick={() => removeLook(l.id)} aria-label="Remove look"
                                    className="absolute top-2 right-2 p-2 rounded-full bg-black/60 text-white hover:bg-brand-magenta transition-colors opacity-0 group-hover:opacity-100">
                                    <X className="w-3.5 h-3.5" />
                                </button>
                                {l.startup_id && (
                                    <Link to={`/startup/${l.startup_id}`} className="absolute bottom-2 left-2 right-2 font-mono-tag text-[10px] uppercase text-white/80 hover:text-brand-saffron truncate">
                                        {l.title || "View brand"} <ArrowUpRight className="inline w-3 h-3" />
                                    </Link>
                                )}
                            </div>
                        ))}
                    </div>
                )}
            </section>

            {/* Following */}
            <section className="mt-14">
                <p className="font-mono-tag text-xs uppercase text-white/50 mb-4">/ following ({following.length})</p>
                {following.length === 0 ? (
                    <div className="border border-dashed border-white/15 rounded-md py-16 text-center">
                        <p className="font-display text-2xl mb-2">Not following anyone yet.</p>
                        <Link to="/discover" className="text-brand-saffron font-mono-tag text-xs uppercase hover:underline">Discover brands →</Link>
                    </div>
                ) : (
                    <div data-testid="profile-following-list" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {following.map((b) => (
                            <Link key={b.id} to={`/startup/${b.id}`} data-testid={`following-brand-${b.id}`}
                                className="grain-card rounded-md p-4 flex items-center gap-4 hover:border-brand-saffron transition-colors">
                                {(b.preview_images?.[0] || b.banner_url) ? (
                                    <img src={b.preview_images?.[0] || b.banner_url} alt={b.brand_name} className="w-14 h-14 rounded object-cover" />
                                ) : (
                                    <div className="w-14 h-14 rounded bg-brand-ink border border-white/10 flex items-center justify-center font-serif-editorial italic text-xl text-white/25">{b.brand_name?.[0]}</div>
                                )}
                                <div className="flex-1 min-w-0">
                                    <p className="font-display font-bold text-lg tracking-tight truncate">{b.brand_name}</p>
                                    <p className="text-white/50 text-xs truncate">{b.tagline}</p>
                                </div>
                                <Heart className="w-4 h-4 text-brand-magenta fill-current shrink-0" />
                            </Link>
                        ))}
                    </div>
                )}
            </section>
        </main>
    );
}
